import { useEffect } from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useSelector } from 'react-redux';
import Head from 'next/head';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@material-ui/core';
import initApollo from '../../apollo/initApollo';
import VehicleBreadcrumbs from '../../components/VehicleBreadcrumbs';
import VehicleDataSheet from '../../components/VehicleDataSheet';
import VehicleDescription from '../../components/VehicleDescription';
import VehicleMeta from '../../components/VehicleMeta';
import VehicleSpecsTable from '../../components/VehicleSpecsTable';
import VehicleSummary from '../../components/VehicleSummary';
import useGetVehicle from '../../hooks/useGetVehicle';
import SearchLayout from '../../layouts/SearchLayout';
import getVehicleQuery from '../../queries/getVehicleQuery';
import VehiclesWebComponent from '../../components/VehiclesWebComponent';
import VehicleGallery from '../../components/VehicleGallery';
import VehicleFinancing from '../../components/VehicleFinancing';

export const getServerSideProps = async ({ params }) => {
  const client = initApollo(process.env.API_URL);

  const [, id] = params.vehicle;

  await client.query({
    query: getVehicleQuery,
    variables: { vehicleId: id },
  });

  return {
    props: {
      initialApolloState: client.cache.extract(),
    },
  };
};

export default function VehicleDetailsPage() {
  const { t } = useTranslation('details');
  const { error, loading, vehicle } = useGetVehicle();
  const financingAvailable = useSelector((state) => state.vehicle.financingAvailable);

  useEffect(() => {
    document.querySelector('#top')?.scrollIntoView({ block: 'end' });
  }, []);

  if (loading)
    return (
      <SearchLayout>
        <CircularProgress />
      </SearchLayout>
    );

  if (error) return <SearchLayout>error</SearchLayout>;

  if (!vehicle)
    return (
      <SearchLayout>
        <div className="mx-auto text-center text-background-text">
          <p className="text-4xl">Ooops!</p>
          <div className="w-full h-96 relative">
            <Image src="/crashedCar.svg" layout="fill" />
          </div>

          <p className="text-2xl mb-6">Parece que no hay nada por aquí</p>
          <Link href="/buscar">
            <Button className="text-xl mb-8" variant="outlined">
              Buscar autos
            </Button>
          </Link>
        </div>
      </SearchLayout>
    );

  return (
    <>
      <VehicleMeta />
      <Head>
        <script async src="https://static.addtoany.com/menu/page.js" />
        <script src="https://intelimotor.sfo2.cdn.digitaloceanspaces.com/funnel-components/latest/script.js" />
        <script src="https://intelimotor.sfo2.cdn.digitaloceanspaces.com/intelimotor-components/latest/build/script.js" />
        {/* use this for testing */}
        {/* <script src="http://localhost:5000/build/script.js" /> */}

        <script
          type="text/javascript"
          src="https://platform-api.sharethis.com/js/sharethis.js#property=66393b66337e7f00199c829d&product=inline-share-buttons&source=platform"
          async="async"
        />
      </Head>
      <SearchLayout>
        <div className="w-full">
          <div className="p-2 pt-0">
            <VehicleBreadcrumbs />
          </div>
          <Card className="p-4 mb-8" variant="outlined">
            <div className="md:flex mb-12">
              <div className="flex-grow md:w-2/3">
                <VehicleGallery />
              </div>
              <div id="vehicleSummary" className="md:ml-12 md:w-1/3">
                <VehicleSummary />
              </div>
            </div>
            <div
              className={`flex flex-col-reverse md:flex-row ${financingAvailable ? 'gap-4' : ''}`}
            >
              <div
                className={`flex flex-col gap-4 ${financingAvailable ? 'md:w-2/3' : 'md:w-full'}`}
              >
                <VehicleDescription />
                <VehicleDataSheet />
                <VehicleSpecsTable type="equipment" />
                <VehicleSpecsTable type="confort" />
                <VehicleSpecsTable type="exterior" />
                <VehicleSpecsTable type="security" />
                <VehicleSpecsTable type="stereo" />
              </div>
              {financingAvailable && (
                <div className="md:w-1/3 mb-4">
                  <VehicleFinancing />
                </div>
              )}
            </div>
          </Card>

          <div className="mb-12">
            <div className="text-2xl mb-4 px-4 lg:px-0">{t('Related Vehicles')}</div>
            <VehiclesWebComponent bodyTypes={[vehicle.bodyType]} />
          </div>
        </div>
      </SearchLayout>
    </>
  );
}
